import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircle, faCircleDot, faEyeSlash, faExpandArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import { motion, AnimatePresence } from 'framer-motion';
import houses from '../../../questions/astrology/houses.json';
import Question from '../../../components/Question';
import ChartDrawer from '../../../../components/tools/ChartDrawer';
const EleventhHouseLesson = ({ level = 'beginner', onNextLesson, markLessonComplete }) => {
  const houseData = houses.find(house => house.name === "Eleventh House");
  const sections = houseData.sections || [];

  const [currentSection, setCurrentSection] = useState(0);
  const [feedback, setFeedback] = useState(null);
  const [completedSections, setCompletedSections] = useState(Array(sections.length).fill(false));
  const [imageVisible, setImageVisible] = useState(true);
  const [imageSize, setImageSize] = useState('smallimage');

  const handleSectionChange = (newSectionIndex) => {
    setCurrentSection(newSectionIndex);
    setFeedback(null);
  };

  const handleAnswer = (isCorrect) => {
    if (isCorrect) {
      setFeedback("Correct!");
      const newCompletedSections = [...completedSections];
      newCompletedSections[currentSection] = true;
      setCompletedSections(newCompletedSections);
      if (currentSection === sections.length - 1) {
        markLessonComplete();
        onNextLesson();
      } else {
        handleSectionChange(currentSection + 1);
      }
    } else {
      setFeedback(`Incorrect. The correct answer is: ${sections[currentSection].questions[level].correctAnswer}`);
    }
  };

  const handleDotClick = (index) => {
    if (index <= currentSection || completedSections[index - 1]) {
      handleSectionChange(index);
    }
  };

  const toggleImageVisibility = () => {
    setImageVisible(!imageVisible);
  };

  const toggleImageSize = () => {
    setImageSize(imageSize === 'smallimage' ? 'largeimage' : 'smallimage');
  };

  const transition = { duration: 0.15, ease: "easeInOut" };

  return (
    <div>
      <Helmet>
        <title>{houseData.name} - {level.charAt(0).toUpperCase() + level.slice(1)} Lesson</title>
      </Helmet>

      <AnimatePresence mode="wait">
        <motion.div
          key={currentSection}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        >
          {imageVisible && (
            <img
              src={houseData.imageUrl}
              alt={houseData.name}
              className={imageSize}
            />
          )}
          <div className="icon-buttons">
            <FontAwesomeIcon
              icon={faEyeSlash}
              className="icon-button"
              onClick={toggleImageVisibility}
            />
            <FontAwesomeIcon
              icon={faExpandArrowsAlt}
              className="icon-button"
              onClick={toggleImageSize}
            />
          </div>

          <section className="section has-text-centered" id={sections[currentSection].id}>
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <h2 className="has-text-primary">{sections[currentSection].title}</h2>
                  <p className="has-text-white">{sections[currentSection].content}</p>
                </div>
              </div>
            </div>
          </section>

          <section className="section has-text-centered">
            <div className="container content">
              <div className="columns">
                <div className="column">
                  <Question
                    question={sections[currentSection].questions[level].question}
                    options={sections[currentSection].questions[level].options}
                    correctAnswer={sections[currentSection].questions[level].correctAnswer}
                    onAnswered={handleAnswer}
                  />
                  {feedback && <p className="mt-2 has-text-warning">{feedback}</p>}
                </div>
              </div>
            </div>
          </section>

          <section className="section has-text-centered">
            <div className="column">
              <div className="dots mt-4">
                {sections.map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={completedSections[index] ? faCircleDot : faCircle}
                    className={`mr-2 ${index === currentSection ? 'has-text-primary' : completedSections[index] ? 'has-text-warning' : 'has-text-grey'} dot-icon`}
                    onClick={() => handleDotClick(index)}
                  />
                ))}
              </div>
            </div>
          </section>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default EleventhHouseLesson;
